import { push } from 'react-router-redux';

import { SECTIONS_TYPE } from 'containers/Checklist/constants';
import {
  checklistItemChanged,
  updateStorageChecklistItem,
} from 'containers/Checklist/actions';
import ROUTES from 'routes/constants';
import { getUser } from 'utils/user';

import {
  COMPLETE_ASSESSMENT,
  LOAD_ASSESSMENT,
  SECTIONS,
  SECTION_TO_SLUG_MAP,
  SET_HELP_DIALOG_CLOSE,
  SET_HELP_DIALOG_OPEN,
  SET_ITEM_VALUE,
  TRACK_CHECKLIST_ASSESSMENT_PAGE_VIEWED,
  TRACK_CHECKLIST_GENERAL_CONDITIONS_CLOSE_BUTTON_CLICKED,
  TRACK_CHECKLIST_GENERAL_CONDITIONS_FINISH_BUTTON_CLICKED,
  TRACK_CHECKLIST_GENERAL_CONDITIONS_FINISH_CONFIRMED,
  CONSERVATION_TYPES,
  STATUS,
  MAINTENANCE_CONDITION,
  TRACK_CHECKLIST_ASSESSMENT_CONFIRMATION_PAGE_VIEWED,
  TRACK_CHECKLIST_GENERAL_CONDITIONS_ERROR_CTA_VIEWED,
  TRACK_CHECKLIST_GENERAL_CONDITIONS_DOUBTS_BUTTON_CLICKED,
  TRACK_CHECKLIST_USEFUL_INFORMATION_BUTTON_CLICKED,
  UPDATE_ASSESSMENT_V2_ENABLED,
} from './constants';
import {
  makeSelectAssessment,
  makeSelectIsCompleted,
  makeSelectResumeSectionId,
} from './selectors';

export const setIsHelpDialogOpen = () => ({
  type: SET_HELP_DIALOG_OPEN,
});

export const setIsHelpDialogClose = () => ({
  type: SET_HELP_DIALOG_CLOSE,
});

export const setItemValue = ({ photoSessionId, item, onUpdate }) => ({
  type: SET_ITEM_VALUE,
  promise: Promise.resolve({ photoSessionId, item }),
  meta: {
    onSuccess: (_, getState) => {
      const state = getState();
      const updatedAssessment = makeSelectAssessment(photoSessionId)(state);

      onUpdate(updatedAssessment);
    },
  },
});

export const loadAssessment = ({
  photoSessionId,
  assessment,
  checklist,
  onUpdate,
  isAssessmentV2Enabled = false,
}) => ({
  type: LOAD_ASSESSMENT,
  promise: Promise.resolve({
    photoSessionId,
    assessment,
    checklist,
    isAssessmentV2Enabled,
  }),
  meta: {
    onSuccess: (_, getState) => {
      const state = getState();
      const updatedAssessment = makeSelectAssessment(photoSessionId)(state);

      onUpdate(updatedAssessment);
    },
  },
});

export const resumeAssessment =
  ({ photoSessionId }) =>
    (dispatch, getState) => {
      const state = getState();
      const isCompleted = makeSelectIsCompleted(photoSessionId)(state);

      const sectionId = makeSelectResumeSectionId(photoSessionId)(state);
      const resumeSectionId = isCompleted
        ? SECTIONS.ELECTRICAL
        : sectionId || SECTIONS.ELECTRICAL;

      dispatch(
        push(
          ROUTES.PROPERTY_ASSESSMENT_SECTION(
            photoSessionId,
            SECTION_TO_SLUG_MAP[resumeSectionId],
          ),
        ),
      );
    };

export const trackChecklistGeneralConditionsCloseButtonClicked = ({
  houseId,
  houseArea,
  currentPage,
  category,
}) => ({
  type: TRACK_CHECKLIST_GENERAL_CONDITIONS_CLOSE_BUTTON_CLICKED,
  eventProperties: {
    houseId,
    houseArea,
    currentPage,
    category,
  },
});

export const trackChecklistGeneralConditionsFinishButtonClicked = ({
  houseId,
  houseArea,
}) => ({
  type: TRACK_CHECKLIST_GENERAL_CONDITIONS_FINISH_BUTTON_CLICKED,
  eventProperties: {
    houseId,
    houseArea,
  },
});

export const trackChecklistGeneralConditionsFinishConfirmed = ({
  houseId,
  houseArea,
}) => ({
  type: TRACK_CHECKLIST_GENERAL_CONDITIONS_FINISH_CONFIRMED,
  eventProperties: {
    houseId,
    houseArea,
  },
});

export const completeAssessment = ({ photoSessionId }) => ({
  type: COMPLETE_ASSESSMENT,
  payload: {
    photoSessionId,
  },
});

export const updateIsAssessmentV2Enabled = ({
  photoSessionId,
  isAssessmentV2Enabled,
}) => ({
  type: UPDATE_ASSESSMENT_V2_ENABLED,
  payload: {
    photoSessionId,
    isAssessmentV2Enabled,
  },
});

const MAINTENANCE_CONDITION_RANGE_MAP = Object.freeze({
  [MAINTENANCE_CONDITION.PERFECT_STATE]: { min: 3.8, max: 4 },
  [MAINTENANCE_CONDITION.ALMOST_PERFECT_STATE]: { min: 3.2, max: 3.8 },
  [MAINTENANCE_CONDITION.NEW_OR_WITHOUT_BASIC_ITENS]: { min: 2.5, max: 3.2 },
  [MAINTENANCE_CONDITION.OLD]: { min: 2, max: 2.5 },
  [MAINTENANCE_CONDITION.POORLY_MAINTAINED]: { min: 0, max: 2 },
});

const CONSERVATION_TO_SCORE_MAP = Object.freeze({
  [CONSERVATION_TYPES.NEEDS_REPAIR]: 1,
  [CONSERVATION_TYPES.MANY_SIGNS_OF_USE]: 2,
  [CONSERVATION_TYPES.FEW_SIGNS_OF_USE]: 3,
  [CONSERVATION_TYPES.NEW_ITEM]: 4,
});

export const updateMaintenanceCondition =
  ({ photoSessionId }) =>
    (dispatch, getState) => {
      const userId = getUser().user_id;

      const assessment = makeSelectAssessment(photoSessionId)(getState());

      if (!assessment || assessment.status === STATUS.COMPLETED) {
        return;
      }

      let totalItems = 0;
      let totalScores = 0;

      Object.values(assessment.sections).forEach((section) => {
        Object.values(section.items).forEach((item) => {
          if (item.id.includes('CONSERVATION')) {
            totalItems += 1;
            totalScores += CONSERVATION_TO_SCORE_MAP[item.value] || 0;
          }
        });
      });

      const score = totalScores / totalItems;
      let maintenanceCondition = '';

      // eslint-disable-next-line no-restricted-syntax
      for (const [value, { min, max }] of Object.entries(
        MAINTENANCE_CONDITION_RANGE_MAP,
      )) {
        if (score >= min && score <= max) {
          maintenanceCondition = value;
          break;
        }
      }

      const params = {
        sectionType: SECTIONS_TYPE.GENERAL_CONDITIONS,
        section: 'propertyMaintananceCondition',
        itemSlug: 'maintenanceCondition',
        value: maintenanceCondition,
        photoSessionId,
        userId,
      };

      dispatch(checklistItemChanged(params));
      dispatch(updateStorageChecklistItem(params));
    };

export const trackChecklistAssessmentPageViewed = ({
  houseId,
  houseArea,
  sectionId,
}) => ({
  type: TRACK_CHECKLIST_ASSESSMENT_PAGE_VIEWED,
  eventProperties: {
    houseId,
    houseArea,
    sectionId,
  },
});

export const trackChecklistAssessmentConfirmationPageViewed = ({
  houseId,
  houseArea,
}) => ({
  type: TRACK_CHECKLIST_ASSESSMENT_CONFIRMATION_PAGE_VIEWED,
  eventProperties: {
    houseId,
    houseArea,
  },
});

export const trackChecklistGeneralConditionsErrorCTAViewed = ({
  houseId,
  houseArea,
  currentPage,
  category,
  errorType,
}) => ({
  type: TRACK_CHECKLIST_GENERAL_CONDITIONS_ERROR_CTA_VIEWED,
  eventProperties: {
    houseId,
    houseArea,
    currentPage,
    category,
    errorType,
  },
});

export const trackChecklistGeneralConditionsDoubtsButtonClicked = ({
  houseId,
  houseArea,
  currentPage,
  category,
}) => ({
  type: TRACK_CHECKLIST_GENERAL_CONDITIONS_DOUBTS_BUTTON_CLICKED,
  eventProperties: {
    houseId,
    houseArea,
    currentPage,
    category,
  },
});

export const trackChecklistUsefulInformationButtonClicked = ({
  useful,
  checklistItem,
  category,
}) => ({
  type: TRACK_CHECKLIST_USEFUL_INFORMATION_BUTTON_CLICKED,
  eventProperties: {
    useful,
    checklistItem,
    category,
  },
});
